// src/services/contractGeneration.service.ts
// Generates service agreement PDFs and amendment PDFs from project data

import { db, storage } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import { Project } from '../types/project.types';
import { Organization } from '../types/organization.types';
import { Contract } from '../types/contract.types';
import { ServiceAgreementVars } from '../utils/serviceAgreementTemplate';
import { AmendmentVars } from '../utils/amendmentTemplate';

// Format currency for display
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// Format a Firestore Timestamp or Date as a readable date string
function formatDate(dateVal: any): string {
  if (!dateVal) return 'TBD';
  const date = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
  return format(date, 'MMMM d, yyyy');
}

// Build the full address string for an organization
function buildOrgAddress(org: Organization): string {
  if (!org.address) return 'Address not provided';
  const { street, city, state, zipCode } = org.address;
  return [street, city, state, zipCode].filter(Boolean).join(', ');
}

// ====================================================
// PDF GENERATION HELPERS (jsPDF text rendering)
// ====================================================

interface PDFSection {
  type: 'title' | 'heading' | 'subheading' | 'paragraph' | 'bullet' | 'signature-block';
  text: string;
  bold?: boolean;
}

function buildServiceAgreementSections(vars: ServiceAgreementVars): PDFSection[] {
  const sections: PDFSection[] = [];

  sections.push({ type: 'title', text: 'LEAD GENERATION SERVICES AGREEMENT' });
  sections.push({ type: 'subheading', text: 'Canvassed, LLC' });

  sections.push({ type: 'paragraph', text: `This Lead Generation Services Agreement ("Agreement") is entered into as of ${vars.effectiveDate} by and between:` });
  sections.push({ type: 'paragraph', text: `Canvassed, LLC, a Florida limited liability company, with its principal place of business at 5200 NW 43rd Street, Suite 102-241, Gainesville, FL 32606 ("Provider"); and` });
  sections.push({ type: 'paragraph', text: `${vars.clientEntityName}, ${vars.clientEntityType}, with its principal place of business at ${vars.clientAddress} ("Client").` });
  sections.push({ type: 'paragraph', text: `By executing this Agreement electronically, Client acknowledges and agrees to all terms and conditions set forth herein. Provider's acceptance is evidenced by the delivery of this Agreement for execution.` });

  // Section 1: Term
  sections.push({ type: 'heading', text: '1. Term' });
  sections.push({ type: 'paragraph', text: `1.1 This Agreement shall commence on ${vars.startDate} and continue until ${vars.endDate}, unless terminated earlier in accordance with the terms herein or extended by mutual written agreement of the parties.` });

  // Section 2: Scope of Services
  sections.push({ type: 'heading', text: '2. Scope of Services' });
  sections.push({ type: 'paragraph', text: '2.1 Provider agrees to provide the following services to Client:' });
  sections.push({ type: 'bullet', text: 'Lead Generation: Deploy a team of qualified canvassers to targeted communities, bilingual if necessary, to collect Leads from parents interested in enrolling their children at the Client\'s school.' });
  sections.push({ type: 'bullet', text: 'Community Engagement: Engage with the community through traditional canvassing, precise targeting, and innovative outreach (e.g., micro-events) to build trust and generate interest in enrollment at the Client\'s school.' });
  sections.push({ type: 'bullet', text: 'Data Collection and Verification: Collect paper or digital interest forms from parents, ensuring all required details are accurate and complete. Each Lead (student) will be assigned a unique identifier by the Provider for internal tracking.' });
  sections.push({ type: 'bullet', text: 'Lead Delivery via Platform: All Leads will be delivered to Client through the Canvassed platform ("Platform"). Client will receive access credentials and is responsible for reviewing, dispositioning, and managing Leads within the Platform as described in § 5.' });
  sections.push({ type: 'bullet', text: 'Multiple Engagement Cycles: Revisit key locations and target communities multiple times to strengthen relationships and maximize Lead collection.' });
  sections.push({ type: 'bullet', text: 'Lead Validation: Confirm in writing with each prospective parent that they are interested in learning more about enrolling their children at the Client\'s school.' });
  sections.push({ type: 'paragraph', text: `2.2 The Provider will use commercially reasonable efforts to deliver at least ${vars.leadQuantity.toLocaleString()} Valid Leads ("Deliverables") during the term of this Agreement, with each Lead representing one student as defined in § 4.1.1.` });

  // Section 3: Pricing and Payment Terms
  sections.push({ type: 'heading', text: '3. Pricing and Payment Terms' });
  sections.push({ type: 'paragraph', text: `3.1 Client agrees to pay Provider ${vars.pricePerLead} per Valid Lead, where each Lead is a student listed on a Valid Interest Form as defined in § 4.1.` });
  sections.push({ type: 'paragraph', text: '3.2 Invoicing: Invoices will be issued on Mondays for the prior week\'s services and are payable within 7 business days via electronic transfer. Services may pause if funds are not received on time.' });
  sections.push({ type: 'paragraph', text: '3.3 Conversion Rate Adjustment: If the Conversion Rate of Valid Leads falls below 20% within 90 days after the Agreement\'s termination or the last Lead submission, Provider will provide additional Leads at no cost until either (a) the Conversion Rate reaches 20%, or (b) the total number of additional Leads provided reaches 40% of the number of Leads initially contracted, whichever occurs first, provided Client has complied with its follow-up obligations under § 5.1.' });
  sections.push({ type: 'paragraph', text: '3.4 Auto-Validation: Leads not dispositioned by Client in the Platform within two (2) business days of delivery—or by the scheduled contact time, whichever applies—shall be automatically marked as "Valid and Enrolled" and deemed valid for payment and conversion purposes.' });
  sections.push({ type: 'paragraph', text: '3.5 The parties acknowledge that the conversion process naturally involves a delay, typically requiring between 4 to 12 touches to convert a student into an enrollment.' });
  sections.push({ type: 'paragraph', text: `3.6 Budget Cap: The total amount payable under this Agreement shall not exceed ${vars.budgetCap}, unless otherwise agreed in writing by both parties.` });

  // Section 4: Definitions
  sections.push({ type: 'heading', text: '4. Definitions' });
  sections.push({ type: 'paragraph', text: '4.1 For the purposes of this Agreement:' });
  sections.push({ type: 'paragraph', text: '4.1.1 Valid Interest Form (or "Valid Lead"): A completed interest form that is legible, timely submitted, and enables Client to contact the parent, who confirms interest in enrolling at least one eligible child at the Client\'s school.' });
  sections.push({ type: 'paragraph', text: '4.1.2 Lead: Each student listed on a Valid Interest Form is considered a separate Lead for payment and conversion calculations.' });
  sections.push({ type: 'paragraph', text: '4.1.3 Conversion Rate: The percentage of Leads that result in enrollment of that student at the Client\'s school.' });
  sections.push({ type: 'paragraph', text: '4.1.4 Platform: The Canvassed software application through which Leads are delivered, tracked, dispositioned, and reported.' });
  sections.push({ type: 'paragraph', text: '4.1.5 Disposition: The contact status assigned to a Lead by the Client within the Platform.' });
  sections.push({ type: 'paragraph', text: '4.1.6 Determination: The enrollment outcome assigned to a Lead by the Client within the Platform.' });

  // Section 5: Obligations
  sections.push({ type: 'heading', text: '5. Obligations' });
  sections.push({ type: 'paragraph', text: '5.1 Client Agrees to:', bold: true });
  sections.push({ type: 'bullet', text: 'Provide a PDF copy of their current student enrollment interest form for Provider to circulate.' });
  sections.push({ type: 'bullet', text: 'Use the Platform as the sole system of record for Lead tracking. For each Lead, Client shall update the Disposition and Determination within two (2) business days of Lead delivery.' });
  sections.push({ type: 'bullet', text: 'Provide a current version of the scripting their internal team uses while canvassing.' });
  sections.push({ type: 'bullet', text: 'Provide a current detailed report on the targeted areas with a priority level assigned to each target area and region.' });
  sections.push({ type: 'bullet', text: 'Provide a written statement detailing all contraindicated actions specific to the Client\'s marketplace.' });
  sections.push({ type: 'bullet', text: 'Provide a detailed information memo sufficient to allow the Provider to train its staff on the Client\'s school.' });
  sections.push({ type: 'bullet', text: 'Accept responsibility for converting Leads into students post-delivery by the Provider.' });

  sections.push({ type: 'paragraph', text: '5.2 Provider Agrees to:', bold: true });
  sections.push({ type: 'bullet', text: 'Deliver the services as described in the Scope of Services and comply with the requirements outlined in this Agreement.' });
  sections.push({ type: 'bullet', text: 'Ensure all Leads are verified and meet the criteria for validity before submission to Client.' });
  sections.push({ type: 'bullet', text: 'Extend commercially reasonable efforts to assist the Client in converting Leads.' });
  sections.push({ type: 'bullet', text: 'Deliver all Leads through the Platform with accurate and complete data.' });

  // Section 6: Reporting
  sections.push({ type: 'heading', text: '6. Reporting and Platform Usage' });
  sections.push({ type: 'paragraph', text: '6.1 Client shall maintain current and accurate records in the Platform at all times, updating each Lead\'s Disposition and Determination within two (2) business days of Lead delivery.' });
  sections.push({ type: 'paragraph', text: '6.2 Provider may audit Client\'s Lead follow-up process through the Platform upon reasonable notice.' });
  sections.push({ type: 'paragraph', text: '6.3 If Provider disputes a Client determination that a Lead is invalid, Provider may submit a written challenge within 14 days. Client shall provide verifiable evidence within 5 business days.' });
  sections.push({ type: 'paragraph', text: '6.4 Provider shall deliver Leads via the Platform on each day Leads are gathered.' });

  // Section 7: Termination
  sections.push({ type: 'heading', text: '7. Termination' });
  sections.push({ type: 'paragraph', text: '7.1 Either party may terminate this Agreement without cause upon ten (10) days\' written notice. Client shall pay Provider for all services rendered up to the date of termination. Either party may terminate based on the other party\'s material breach not cured within three (3) days\' notice.' });

  // Section 8: Reliance
  sections.push({ type: 'heading', text: '8. Reliance and Investment' });
  sections.push({ type: 'paragraph', text: '8.1 The Provider is relying on the Client\'s commitment and investing resources including flights, corporate housing, and other non-recoverable expenses. If the Client terminates without cause and without ten (10) days\' advance written notice, Provider shall be entitled to reimbursement for its initial capital expenses at cost basis.' });

  // Section 9: Non-Solicitation
  sections.push({ type: 'heading', text: '9. Non-Solicitation' });
  sections.push({ type: 'paragraph', text: '9.1 Client agrees not to hire or solicit Provider\'s staff during the term and for two (2) years following termination. Violation results in a fee equal to 10% of the staff member\'s annual salary for the first year.' });

  // Section 10: Dispute Resolution
  sections.push({ type: 'heading', text: '10. Dispute Resolution' });
  sections.push({ type: 'paragraph', text: '10.1 Disputes shall be resolved through mediation in the State of Florida. Joint mediation costs split equally. If mediation fails, parties may seek relief in Alachua County, Florida, with the prevailing party entitled to attorneys\' fees.' });

  // Section 11: Confidentiality
  sections.push({ type: 'heading', text: '11. Confidentiality' });
  sections.push({ type: 'paragraph', text: '11.1 Both parties agree to maintain confidentiality of proprietary or sensitive information shared during the term, including parent data, business strategies, and Platform access credentials.' });

  // Section 12: Decoy Leads
  sections.push({ type: 'heading', text: '12. Decoy Leads' });
  sections.push({ type: 'paragraph', text: '12.1 Provider may submit up to five (5) unannounced decoy Leads to verify Client\'s follow-up obligations.' });
  sections.push({ type: 'paragraph', text: '12.2 Purpose: Decoy Leads test the Client\'s follow-up process and will not be included in payment or performance metrics.' });
  sections.push({ type: 'paragraph', text: '12.3 Billing: Provider will not invoice for decoy Leads, and they will not count toward the total Leads provided.' });
  sections.push({ type: 'paragraph', text: '12.4 Notification: Provider will inform the Client which Leads were decoys after the follow-up period.' });

  // Section 13: Miscellaneous
  sections.push({ type: 'heading', text: '13. Miscellaneous' });
  sections.push({ type: 'paragraph', text: '13.1 Governing Law: This Agreement shall be governed by the laws of the State of Florida.' });
  sections.push({ type: 'paragraph', text: '13.2 Entire Agreement: This Agreement constitutes the entire agreement between the parties.' });
  sections.push({ type: 'paragraph', text: '13.3 Amendments: Any amendments must be in writing and agreed to by both parties.' });
  sections.push({ type: 'paragraph', text: '13.4 Force Majeure: If Provider\'s performance is prevented for more than five (5) consecutive days due to natural disasters or similar events, Provider may propose remedies including price adjustments.' });
  sections.push({ type: 'paragraph', text: '13.5 Operational Discretion: Provider may temporarily pause operations as needed for business needs without affecting obligations.' });
  sections.push({ type: 'paragraph', text: '13.6 Electronic Execution: This Agreement may be executed electronically with the same legal effect as original signatures.' });
  sections.push({ type: 'paragraph', text: '13.7 Platform Terms: Client\'s use of the Platform is subject to Provider\'s standard terms of service.' });

  // Execution block
  sections.push({ type: 'signature-block', text: vars.clientEntityName });

  return sections;
}

function buildAmendmentSections(vars: AmendmentVars): PDFSection[] {
  const sections: PDFSection[] = [];

  sections.push({ type: 'title', text: `AMENDMENT NO. ${vars.amendmentNumber}` });
  sections.push({ type: 'subheading', text: 'TO LEAD GENERATION SERVICES AGREEMENT' });

  sections.push({ type: 'paragraph', text: `This Amendment No. ${vars.amendmentNumber} ("Amendment") to the Lead Generation Services Agreement dated ${vars.originalContractDate} ("Original Agreement") is entered into as of ${vars.effectiveDate} by and between:` });
  sections.push({ type: 'paragraph', text: `Canvassed, LLC, a Florida limited liability company, with its principal place of business at 5200 NW 43rd Street, Suite 102-241, Gainesville, FL 32606 ("Provider"); and` });
  sections.push({ type: 'paragraph', text: `${vars.clientEntityName}, ${vars.clientEntityType}, with its principal place of business at ${vars.clientAddress} ("Client").` });

  sections.push({ type: 'heading', text: 'RECITALS' });
  sections.push({ type: 'paragraph', text: 'WHEREAS, Provider and Client entered into the Original Agreement for lead generation services; and' });
  sections.push({ type: 'paragraph', text: 'WHEREAS, the parties now wish to amend certain terms of the Original Agreement as set forth below.' });

  sections.push({ type: 'heading', text: 'AMENDED TERMS' });
  sections.push({ type: 'paragraph', text: 'The following terms of the Original Agreement are hereby amended, effective as of the date of this Amendment:' });

  // Render changed terms as formatted text (table simulation)
  for (const term of vars.changedTerms) {
    sections.push({ type: 'bullet', text: `${term.field}: Changed from "${term.oldValue}" to "${term.newValue}"` });
  }

  sections.push({ type: 'heading', text: 'GENERAL PROVISIONS' });
  sections.push({ type: 'paragraph', text: '1. All terms and conditions of the Original Agreement not expressly modified by this Amendment shall remain in full force and effect.' });
  sections.push({ type: 'paragraph', text: '2. In the event of any conflict between the terms of this Amendment and the Original Agreement, the terms of this Amendment shall control.' });
  sections.push({ type: 'paragraph', text: '3. This Amendment may be executed electronically with the same legal effect as original signatures.' });

  sections.push({ type: 'signature-block', text: vars.clientEntityName });

  return sections;
}

function renderSectionsToPDF(pdfDoc: jsPDF, sections: PDFSection[]): void {
  const pageWidth = pdfDoc.internal.pageSize.getWidth();
  const pageHeight = pdfDoc.internal.pageSize.getHeight();
  const margin = 25;
  const textWidth = pageWidth - margin * 2;
  let yPos = margin;

  const checkNewPage = (requiredSpace: number = 20) => {
    if (yPos > pageHeight - requiredSpace) {
      pdfDoc.addPage();
      yPos = margin;
    }
  };

  for (const section of sections) {
    switch (section.type) {
      case 'title':
        checkNewPage(40);
        pdfDoc.setFontSize(16);
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.text(section.text, pageWidth / 2, yPos, { align: 'center' });
        yPos += 10;
        break;

      case 'subheading':
        pdfDoc.setFontSize(12);
        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.text(section.text, pageWidth / 2, yPos, { align: 'center' });
        yPos += 12;
        break;

      case 'heading':
        checkNewPage(25);
        yPos += 5;
        pdfDoc.setFontSize(12);
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.text(section.text, margin, yPos);
        yPos += 8;
        break;

      case 'paragraph': {
        checkNewPage(15);
        pdfDoc.setFontSize(10);
        pdfDoc.setFont('helvetica', section.bold ? 'bold' : 'normal');
        const lines = pdfDoc.splitTextToSize(section.text, textWidth);
        for (const line of lines) {
          checkNewPage(10);
          pdfDoc.text(line, margin, yPos);
          yPos += 5;
        }
        yPos += 3;
        break;
      }

      case 'bullet': {
        checkNewPage(15);
        pdfDoc.setFontSize(10);
        pdfDoc.setFont('helvetica', 'normal');
        const bulletText = `\u2022  ${section.text}`;
        const bulletLines = pdfDoc.splitTextToSize(bulletText, textWidth - 10);
        for (let i = 0; i < bulletLines.length; i++) {
          checkNewPage(10);
          pdfDoc.text(bulletLines[i], margin + 5, yPos);
          yPos += 5;
        }
        yPos += 2;
        break;
      }

      case 'signature-block':
        checkNewPage(80);
        yPos += 10;
        pdfDoc.setDrawColor(0, 0, 0);
        pdfDoc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 15;

        pdfDoc.setFontSize(12);
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.text('EXECUTION', pageWidth / 2, yPos, { align: 'center' });
        yPos += 10;

        pdfDoc.setFontSize(10);
        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.text('IN WITNESS WHEREOF, Client has executed this document electronically.', margin, yPos);
        yPos += 15;

        // Provider side
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.text('PROVIDER:', margin, yPos);
        yPos += 6;
        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.text('Canvassed, LLC', margin, yPos);
        yPos += 5;
        pdfDoc.text('A Florida limited liability company', margin, yPos);
        yPos += 5;
        pdfDoc.text('By: Jordan, Managing Member', margin, yPos);
        yPos += 15;

        // Client side
        pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.text('CLIENT:', margin, yPos);
        yPos += 6;
        pdfDoc.setFont('helvetica', 'normal');
        pdfDoc.text(section.text, margin, yPos); // Client entity name
        yPos += 10;
        pdfDoc.text('Signature: ___________________________', margin, yPos);
        yPos += 7;
        pdfDoc.text('Printed Name: ___________________________', margin, yPos);
        yPos += 7;
        pdfDoc.text('Title: ___________________________', margin, yPos);
        yPos += 7;
        pdfDoc.text('Date: ___________________________', margin, yPos);
        yPos += 10;
        break;
    }
  }
}

// ====================================================
// PUBLIC API
// ====================================================

export const contractGenerationService = {
  /**
   * Generate a service agreement PDF for a project and create a draft contract in Firestore.
   * Returns the new contract ID.
   */
  async generateServiceAgreementPDF(projectId: string): Promise<string> {
    // 1. Load project
    const projectDoc = await getDoc(doc(db, 'projects', projectId));
    if (!projectDoc.exists()) throw new Error('Project not found');
    const project = { id: projectDoc.id, ...projectDoc.data() } as Project;

    // 2. Load organization
    if (!project.organizationId) throw new Error('Project has no organization');
    const orgDoc = await getDoc(doc(db, 'organizations', project.organizationId));
    if (!orgDoc.exists()) throw new Error('Organization not found');
    const org = { id: orgDoc.id, ...orgDoc.data() } as Organization;

    if (!org.primaryAdminId) throw new Error('Organization has no primary admin set');

    // 3. Load primary admin for signer info
    const adminDoc = await getDoc(doc(db, 'users', org.primaryAdminId));
    const adminData = adminDoc.exists() ? adminDoc.data() : null;
    const adminName = adminData?.name || org.primaryAdminName || 'Organization Admin';
    const adminEmail = adminData?.email || org.contactEmail || '';

    // 4. Build template variables
    const leadQuantity = project.goals?.validLeads || 0;
    const pricePerLead = project.settings?.billingInfo?.ratePerValidLead || 0;
    const budgetCap = leadQuantity * pricePerLead;

    const vars: ServiceAgreementVars = {
      effectiveDate: format(new Date(), 'MMMM d, yyyy'),
      clientEntityName: org.name,
      clientEntityType: org.entityType || 'a corporation',
      clientAddress: buildOrgAddress(org),
      startDate: formatDate(project.settings?.startDate),
      endDate: formatDate(project.settings?.endDate),
      leadQuantity,
      pricePerLead: formatCurrency(pricePerLead),
      budgetCap: formatCurrency(budgetCap)
    };

    // 5. Generate PDF
    const pdfDoc = new jsPDF();
    const sections = buildServiceAgreementSections(vars);
    renderSectionsToPDF(pdfDoc, sections);
    const pdfBlob = pdfDoc.output('blob');

    // 6. Upload to Firebase Storage
    const contractRef = doc(collection(db, 'contracts'));
    const fileName = `service_agreement_${Date.now()}.pdf`;
    const storageRef = ref(storage, `contracts/${contractRef.id}/${fileName}`);
    await uploadBytes(storageRef, pdfBlob);
    const documentUrl = await getDownloadURL(storageRef);

    // 7. Create contract document
    const contractData: Record<string, any> = {
      id: contractRef.id,
      organizationId: project.organizationId,
      projectId,
      title: `Lead Generation Services Agreement - ${org.name}`,
      description: `Service agreement for project: ${project.name}`,
      documentUrl,
      documentName: fileName,
      status: 'draft',
      signatureFields: [],
      signatures: [],
      requiredSigners: [{
        userId: org.primaryAdminId,
        userName: adminName,
        email: adminEmail,
        role: 'client_admin',
        signed: false,
        mandatory: true
      }],
      createdBy: 'system',
      createdByName: 'Canvassed System',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      mandatory: true,
      contractType: 'service_agreement',
      generatedFromTemplate: 'service_agreement',
      contractTerms: {
        effectiveDate: vars.effectiveDate,
        startDate: vars.startDate,
        endDate: vars.endDate,
        leadQuantity,
        pricePerLead,
        budgetCap,
        clientEntityName: org.name,
        clientEntityType: org.entityType || '',
        clientAddress: buildOrgAddress(org)
      }
    };

    await setDoc(contractRef, contractData);

    // 8. Link contract to project
    await updateDoc(doc(db, 'projects', projectId), {
      serviceAgreementId: contractRef.id,
      updatedAt: Timestamp.now()
    });

    return contractRef.id;
  },

  /**
   * Generate an amendment PDF for changed project terms.
   * Returns the new amendment contract ID.
   */
  async generateAmendmentPDF(
    projectId: string,
    changedTerms: { field: string; oldValue: string; newValue: string }[]
  ): Promise<string> {
    // 1. Load project
    const projectDoc = await getDoc(doc(db, 'projects', projectId));
    if (!projectDoc.exists()) throw new Error('Project not found');
    const project = { id: projectDoc.id, ...projectDoc.data() } as Project;

    // 2. Load organization
    if (!project.organizationId) throw new Error('Project has no organization');
    const orgDoc = await getDoc(doc(db, 'organizations', project.organizationId));
    if (!orgDoc.exists()) throw new Error('Organization not found');
    const org = { id: orgDoc.id, ...orgDoc.data() } as Organization;

    if (!org.primaryAdminId) throw new Error('Organization has no primary admin set');

    // 3. Load primary admin
    const adminDoc = await getDoc(doc(db, 'users', org.primaryAdminId));
    const adminData = adminDoc.exists() ? adminDoc.data() : null;
    const adminName = adminData?.name || org.primaryAdminName || 'Organization Admin';
    const adminEmail = adminData?.email || org.contactEmail || '';

    // 4. Load original contract to get date
    let originalContractDate = 'the Original Agreement';
    if (project.serviceAgreementId) {
      const originalDoc = await getDoc(doc(db, 'contracts', project.serviceAgreementId));
      if (originalDoc.exists()) {
        const originalContract = originalDoc.data() as Contract;
        originalContractDate = originalContract.createdAt
          ? format(originalContract.createdAt.toDate(), 'MMMM d, yyyy')
          : 'the Original Agreement';
      }
    }

    // 5. Count existing amendments for sequential numbering
    const amendmentsQuery = query(
      collection(db, 'contracts'),
      where('projectId', '==', projectId),
      where('contractType', '==', 'amendment')
    );
    const amendmentsSnap = await getDocs(amendmentsQuery);
    const amendmentNumber = amendmentsSnap.size + 1;

    // 6. Build amendment variables
    const vars: AmendmentVars = {
      amendmentNumber,
      effectiveDate: format(new Date(), 'MMMM d, yyyy'),
      originalContractTitle: `Lead Generation Services Agreement - ${org.name}`,
      originalContractDate,
      clientEntityName: org.name,
      clientEntityType: org.entityType || 'a corporation',
      clientAddress: buildOrgAddress(org),
      changedTerms
    };

    // 7. Generate PDF
    const pdfDoc = new jsPDF();
    const sections = buildAmendmentSections(vars);
    renderSectionsToPDF(pdfDoc, sections);
    const pdfBlob = pdfDoc.output('blob');

    // 8. Upload to Firebase Storage
    const contractRef = doc(collection(db, 'contracts'));
    const fileName = `amendment_${amendmentNumber}_${Date.now()}.pdf`;
    const storageRef = ref(storage, `contracts/${contractRef.id}/${fileName}`);
    await uploadBytes(storageRef, pdfBlob);
    const documentUrl = await getDownloadURL(storageRef);

    // 9. Create amendment contract document
    const contractData: Record<string, any> = {
      id: contractRef.id,
      organizationId: project.organizationId,
      projectId,
      title: `Amendment No. ${amendmentNumber} - ${org.name}`,
      description: `Amendment to service agreement for project: ${project.name}`,
      documentUrl,
      documentName: fileName,
      status: 'draft',
      signatureFields: [],
      signatures: [],
      requiredSigners: [{
        userId: org.primaryAdminId,
        userName: adminName,
        email: adminEmail,
        role: 'client_admin',
        signed: false,
        mandatory: true
      }],
      createdBy: 'system',
      createdByName: 'Canvassed System',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      mandatory: true,
      contractType: 'amendment',
      generatedFromTemplate: 'amendment',
      amendmentOf: project.serviceAgreementId || '',
      amendmentNumber,
      changedTerms
    };

    await setDoc(contractRef, contractData);

    return contractRef.id;
  },

  /**
   * Send a draft contract to the client for signing.
   * Updates status to pending_signatures and queues an email.
   */
  async sendContractToClient(contractId: string): Promise<void> {
    const contractDoc = await getDoc(doc(db, 'contracts', contractId));
    if (!contractDoc.exists()) throw new Error('Contract not found');
    const contract = contractDoc.data() as Contract;

    // Update status
    await updateDoc(doc(db, 'contracts', contractId), {
      status: 'pending_signatures',
      updatedAt: Timestamp.now()
    });

    // Queue email for each required signer
    for (const signer of contract.requiredSigners) {
      if (!signer.email) continue;

      // Get project name for the email
      let projectName = 'your project';
      if (contract.projectId) {
        const projDoc = await getDoc(doc(db, 'projects', contract.projectId));
        if (projDoc.exists()) {
          projectName = projDoc.data().name || projectName;
        }
      }

      await setDoc(doc(collection(db, 'mail')), {
        to: signer.email,
        template: 'service-agreement-pending',
        templateData: {
          recipientName: signer.userName || 'Client',
          contractTitle: contract.title,
          projectName,
          signUrl: 'https://canvassed-d9cdb.web.app/contracts'
        },
        createdAt: Timestamp.now(),
        status: 'pending'
      });

      // Create in-app notification
      await setDoc(doc(collection(db, 'notifications')), {
        userId: signer.userId,
        type: 'contract_pending',
        title: 'Service Agreement Ready for Signature',
        message: `A service agreement for "${projectName}" is ready for your review and signature.`,
        read: false,
        createdAt: Timestamp.now(),
        metadata: {
          contractId,
          projectId: contract.projectId
        }
      });
    }
  }
};
